import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft, MessageCircle, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ChatList({ user, token }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/conversations`, config);
      setConversations(response.data);
    } catch (error) {
      toast.error('Error al cargar conversaciones');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-[#3A4F6C] text-white p-4 sticky top-0 z-50">
        <div className="container mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Mis Conversaciones</h1>
            <p className="text-sm text-slate-300">Chats de trabajos activos</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto p-4 max-w-2xl">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Cargando conversaciones...</p>
          </div>
        ) : conversations.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <MessageCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-700 mb-2">No tienes conversaciones</h2>
              <p className="text-slate-500 mb-4">
                Las conversaciones se crean automáticamente cuando un trabajador propone un precio y tú lo aceptas.
              </p>
              <Button
                onClick={() => navigate('/workers')}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                Buscar Trabajadores
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <Card
                key={conv.id}
                data-testid={`conversation-${conv.id}`}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/chat/${conv.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Avatar className="h-14 w-14 border-2 border-orange-200">
                        <AvatarFallback className="bg-orange-100 text-orange-700 font-bold">
                          {conv.other_party_name?.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {conv.unread_count > 0 && (
                        <Badge className="absolute -top-1 -right-1 bg-red-500 text-white h-6 w-6 p-0 flex items-center justify-center rounded-full text-xs">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="font-bold text-slate-800 truncate">
                          {conv.other_party_name}
                        </h3>
                        <span className="text-xs text-slate-400">
                          {conv.last_message_at && new Date(conv.last_message_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <p className="text-sm text-orange-600 mb-1 truncate">
                        {conv.job_title}
                      </p>
                      
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-slate-500 truncate flex-1">
                          {conv.last_message || 'Iniciar conversación...'}
                        </p>
                        <Badge className="bg-green-100 text-green-700 ml-2 flex-shrink-0">
                          <DollarSign className="h-3 w-3 mr-1" />
                          ${conv.accepted_price?.toLocaleString()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
